<template>
  <!-- Discover View -->
  <MainLayout
    v-scroll="onScroll"
    scroll-toolbar
    :bg-color="selectedTab === 'ads' ? '' : 'white'"
    class="relative"
    mainLayoutScrollClass="overflow-y-auto overflow-x-hidden"
  >
    <template #toolbar>
      <div class="flex flex-col w-full">
        <!-- Top header -->
        <div class="relative flex items-center justify-between p-3 border-b border-border-normal">
          <!-- Page label, ad counter -->
          <div class="flex items-center gap-3">
            <img
              src="../../assets/icons/discovery.png"
              class="w-8 h-8"
            >
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              Discovery
            </BaseText>
            <!-- Ad Counter -->
            <div class="flex p-0.5 rounded-md bg-neutral-25">
              <BaseText
                type="body"
                size="xs"
                class="text-text-normal px-1.5 py-1"
              >
                Ad Count
              </BaseText>
              <div class="counter-container px-1.5 rounded bg-white">
                <BaseLoadingSpinnerCircle
                  v-if="!getDiscoveryAdCount"
                  :width="18"
                  :height="18"
                  class="text-icon-normal opacity-30"
                  style="margin-top: 2.5px"
                  :duration="0.9"
                />
                <BaseAnimatedCounter
                  v-else
                  class="text-text-muted text-xs font-semibold"
                  :value="getDiscoveryAdCount"
                  :line-height="24"
                  animate-on-mount
                />
              </div>
            </div>
          </div>

          <!-- Discovery Search Bar -->
          <div class="absolute flex top-1/2 transform -translate-y-1/2" style="left: 308px; right: 308px; z-index: 40001">
            <div class="relative flex-grow mx-auto min-w-0 h-8" style="max-width: 600px">
              <SearchBar
                :text-search.sync="textSearch"
                @submit="handleSearchSubmit"
              />
            </div>
          </div>

          <!-- Create New Button -->
          <BaseCreateNewDropdown 
            :disabled="!getIsSubscriptionActive"
            primary
            @save="fetchAdvertisements(false)"
          />

        </div>
        <!-- Subview Tabs -->
        <div
          class="flex gap-2 w-full px-3 py-2"
          :class="{'border-b border-border-normal': selectedTab === 'experts'}"
        >
          <router-link
            :to="{ name: 'DiscoveryView'}"
            class="brand-view-tab"
            :class="{'active': selectedTab === 'ads'}"
          >
            <CommunityFeedIcon class="w-5 h-5" />
            Community Feed
          </router-link>
          <router-link
            :to="{ name: 'DiscoveryExpertsView' }"
            class="brand-view-tab"
            :class="{'active': selectedTab === 'experts'}"
          >
            <ExpertsIcon class="w-5 h-5" />
            Experts
          </router-link>
        </div>
      </div>
    </template>

    <!-- Filter Bar -->
    <template v-if="selectedTab === 'ads'" #filter>
      <div ref="filterBar" class="border border-border-normal bg-white bg-opacity-90" style="backdrop-filter: blur(6px);">
        <FilterTopBar
          ref="filterTopBar"
          no-search
          :keyword-search-opened="openKeywordSearch"
          :text-search="textSearch"
          :active-filters.sync="activeFilters"
          :selected-date-range.sync="selectedDateRange"
          :sort-order.sync="sortOrder"
          @change="fetchAdvertisements"
          @resetKeywordSearch="openKeywordSearch = false"
        />
      </div>
    </template>

    <template #content>
      <div v-if="selectedTab === 'ads'">
        <!-- Loading State -->
        <BaseLoadingLogo
          v-if="loadingAdvertisements || loadingShuffledAds"
          class="mt-24"
        />

        <!-- Empty State With Filters -->
        <div v-else-if="!advertisements.length" class="w-full flex flex-col items-center mt-24 fade-in">
          <img src="https://publicassets.foreplay.co/discovery-empty.png" style="width: 232px; height: 212px;">
          <BaseText type="heading" size="md" class="text-black mt-9">
            {{ textSearch.length ? 'No results found' : 'Too many filters' }}
          </BaseText>
          <BaseText type="body" size="md" class="text-text-normal mt-2 text-center" style="max-width: 390px;">
            We were unable to find any ads that meet your filter or search criteria.
          </BaseText>
          <button class="secondary-btn flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-md mt-9"
          @click="clearFilters">
            <ClearFiltersIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Clear filters
            </BaseText>
          </button>
        </div>

        <!-- Advertisements List -->
        <AdvertisementList
          v-else
          :discovery="true"
          :advertisements="advertisements"
          :loaded="!loadingAdvertisements"
          @updateAds="updateAdsLocal"
          @getMoreAds="handleMoreAdsRequest"
          @reload="fetchAdvertisements(false)"
        />

        <infinite-loading
          v-if="advertisements.length"
          :identifier="infiniteId"
          @infinite="handleInfiniteLoading"
        >
          <div slot="spinner">
            <BaseLoadingLogo :margin="2" />
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
        <transition name="shuffleFade">
          <ShuffleButton
            :can-be-shuffled="isAnySelected"
            @shuffle="fetchShuffledAdvertisements(false)"
            @update:selectedLanguages="updateSelectedLanguages"
            @open-keyword-search="toggleKeywordSearchMethod"
          />
        </transition>
      </div>

      <DiscoveryExperts v-else-if="$route.name === 'DiscoveryExpertsView'" />

      <TutorialModal
        v-if="showTutorialModal"
        @close="showTutorialModal = false"
      />
    </template>
  </MainLayout>
</template>

<script>
import FirebaseAPI from '@/api/firebase'
import InfiniteLoading from 'vue-infinite-loading'
import { mapActions, mapGetters } from 'vuex'
import { SUBSCRIPTION_STATES } from '@/utils/stripe/productMeta'
import posthog from 'posthog-js'
import { clearUniqueCreatives, checkAndCacheAll } from '@/db/UniqueCreativesDB'
import { hasAppliedFilters } from '@/utils/filterUtils'
import axios from 'axios'

import ForeplayAPI from '@/api/foreplayServer'
import firebase from '../../api/config/FirebaseConfig'
import AdvertisementList from '../../components/advertisements/AdvertisementList'
import MainLayout from '../../layouts/MainLayout'
import SearchBar from '../../components/search/SearchBar.vue'
import DiscoveryExperts from '../../components/discovery/DiscoveryExperts.vue'
import TutorialModal from '../../components/advertisements/TutorialModal'
import FilterTopBar from '../../components/search/FilterTopBar.vue'
import ShuffleButton from './ShuffleButton.vue'

// Icons
import CommunityFeedIcon from '../../components/globals/Icons/DiscoveryIcons/CommunityFeedIcon.vue'
import ExpertsIcon from '../../components/globals/Icons/DiscoveryIcons/ExpertsIcon.vue'
import ClearFiltersIcon from '../../components/globals/Icons/FilterIcons/ClearFiltersIcon.vue'

export default {
  name: 'DiscoveryView',
  components: {
    ShuffleButton,
    AdvertisementList,
    SearchBar,
    DiscoveryExperts,
    FilterTopBar,
    InfiniteLoading,
    TutorialModal,
    MainLayout,
    CommunityFeedIcon,
    ExpertsIcon,
    ClearFiltersIcon
  },
  data () {
    return {
      niches: [],
      advertisements: [],
      adsCancelToken: null,
      loadingAdvertisements: false,
      loadingShuffledAds: false,
      showTutorialModal: false,
      activeFilters: {},
      openKeywordSearch: false,
      selectedLanguages: [],
      selectedDateRange: {
        start: null,
        end: null
      },
      sortOrder: [],
      lastDocId: null,
      infiniteId: +new Date(),
      selectedTab: '', // Intitialized in mounted
      docOrder: 'desc',

      // Search Specific
      searchMode: false,
      searchPage: 0,
      searchQuery: '',
      textSearch: '',
      infiniteLoaderState: null,
      isShuffled: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getIsSubscriptionActive']),
    ...mapGetters('SearchModule', ['getFilterCategories']),
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('RouterModule', ['getLastRoute']),
    ...mapGetters('MiscModule', ['getTheme', 'getDiscoveryAdCount', 'getUniqueCreatives']),
    ...mapGetters('AuthModule', [
      'getTeam',
      'isFreeTier',
      'getUserFreeTrialDate',
      'getUser'
    ]),
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('TagsModule', ['getTags']),
    isAnySelected () {
      return (
        Object.keys(this.activeFilters).length > 0 ||
        this.textSearch !== ''
      )
    },
    hasActiveFilters () {
      return hasAppliedFilters(this.activeFilters, this.selectedDateRange)
    }
  },
  watch: {
    // Grouping toggled here or in Settings > Preferences
    getUniqueCreatives () {
      this.fetchAdvertisements()
    },
    getIsSubscriptionActive (newVal, oldVal) {
      // If subscription updates with active state, reset infinite loading
      if (newVal && !oldVal) {
        this.infiniteId += 1
      }
    }
  },
  created () {
    if (this.$route.query.search) {
      this.textSearch = this.$route.query.search
    }
  },
  async mounted () {
    this.selectedLanguages = this.getUser.defaultLanguages || []

    if (this.$route.name === 'DiscoveryExpertsView') {
      this.selectedTab = 'experts'
    } else {
      this.selectedTab = 'ads'
      this.sortOrder = this.textSearch.length
        ? [{ name: 'Most Relevant', value: 'relevance' }]
        : [{ name: 'For You', value: 'best' }]
      await this.fetchAdvertisements()
    }

    // Set initial top bar styles
    if (this.$refs.filterBar) {
      this.$refs.filterBar.style.position = 'sticky'
      this.$refs.filterBar.style.top = '16px'
      this.$refs.filterBar.style.zIndex = '40000'
    }
  },
  beforeDestroy () {
    if (this.adsCancelToken) this.adsCancelToken.cancel()
  },
  methods: {
    // ================================================================================
    // ================================= DATA METHODS =================================
    // ================================================================================

    ...mapActions('BoardsModule', ['fetchBoards']),
    async getNiches () {
      // Setup
      const db = firebase.firestore()

      const snapshot = await db.collection('niches').get()

      const newDocs = snapshot.docs?.map((doc) => {
        const docData = {
          ...(doc.data() || [])
        }

        return { name: docData.niche, icon: docData.emoji }
      })

      const sortedDocs = newDocs.sort((a, b) => (a.name > b.name ? 1 : -1))
      const otherIndex = sortedDocs.findIndex((d) => d.name === 'Other')
      const otherNiche = sortedDocs.splice(otherIndex, 1)

      sortedDocs.push(otherNiche[0])

      return sortedDocs
    },
    // For You only serves the unfiltered feed (the server collapses it to one
    // ad per brand). Once a filter or date range lands while it is selected,
    // fall back to Newest so the dropdown matches what actually runs.
    dropForYouIfFiltered () {
      if (this.sortOrder?.[0]?.value === 'best' && this.hasActiveFilters) {
        this.sortOrder = [{ name: 'Newest', value: 'desc' }]
      }
    },
    handleSearchSubmit () {
      this.updateRouteQuery()
      this.sortOrder = this.textSearch.length
        ? [{ name: 'Most Relevant', value: 'relevance' }]
        : this.selectedTab === 'ads' && !this.hasActiveFilters
          ? [{ name: 'For You', value: 'best' }]
          : [{ name: 'Newest', value: 'desc' }]
      if (this.selectedTab === 'ads') {
        this.fetchAdvertisements()
      }

      // PostHog tracking
      if (this.textSearch.length) {
        posthog.capture('Discovery Search', {
          search_term: this.textSearch,
          tab: this.selectedTab
        })
      }
    },
    updateRouteQuery () {
      if (!this.textSearch.length) {
        window.history.replaceState({}, '', this.$route.path)
        return
      }
      // Update the route's search query oooooeter with the current textSearch value
      const params = new URLSearchParams(window.location.search)
      params.set('search', this.textSearch)
      const newUrl = `${this.$route.path}?${params.toString()}`
      window.history.replaceState({}, '', newUrl)
    },
    handleInfiniteLoading ($state) {
      if (!this.getIsSubscriptionActive) {
        this.$showAlert({
          type: 'info',
          message: 'Please renew your subscription to continue using this feature.' 
        })
        $state.complete()
        return
      }
      if (this.isShuffled) {
        this.getMoreShuffledAds($state)
      } else {
        this.getMoreAds($state)
      }
    },
    handleMoreAdsRequest () {
      if (this.isShuffled) {
        this.getMoreShuffledAds({})
      } else {
        this.getMoreAds({})
      }
    },
    async getMoreAds ($state) {
      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      const savedBetweenStart = +new Date(this.selectedDateRange.start) || null
      const savedBetweenEnd = +new Date(this.selectedDateRange.end) || null
      const { results, nextPage } = await ForeplayAPI.Ads.getDiscoveryAds(this.lastDocId, {
        'or.language': this.selectedLanguages.map(language => language.name),
        ...this.activeFilters,
        savedBetweenStart,
        savedBetweenEnd,
        textSearch: this.textSearch,
        sort: this.sortOrder[0].value
      })

      this.lastDocId = nextPage

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      if (this.getUniqueCreatives) {
        const uniqueCreatives = await checkAndCacheAll(results)
        this.advertisements = [...this.advertisements, ...uniqueCreatives]
      } else {
        this.advertisements = [...this.advertisements, ...results]
      }
      if (Object.keys($state).length) {
        $state.loaded()
      }
    },
    async getMoreShuffledAds ($state) {
      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      const savedBetweenStart = +new Date(this.selectedDateRange.start) || null
      const savedBetweenEnd = +new Date(this.selectedDateRange.end) || null
      const { results, nextPage } = await ForeplayAPI.Ads.getShuffledDiscoveryAds(this.lastDocId, {
        'or.language': this.selectedLanguages.map(language => language.name),
        ...this.activeFilters,
        savedBetweenStart,
        savedBetweenEnd,
        textSearch: this.textSearch,
        sort: this.sortOrder[0].value
      })

      this.lastDocId = nextPage

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      if (this.getUniqueCreatives) {
        const uniqueCreatives = await checkAndCacheAll(results)
        this.advertisements = [...this.advertisements, ...uniqueCreatives]
      } else {
        this.advertisements = [...this.advertisements, ...results]
      }
      if (Object.keys($state).length) {
        $state.loaded()
      }
    },
    // Handle Clear Filters
    async clearFilters () {
      const filterTopBar = this.$refs.filterTopBar
      filterTopBar.clearAllFilters()
      this.textSearch = ''
      this.updateRouteQuery()

      await this.fetchAdvertisements()

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    },
    async fetchShuffledAdvertisements (load = true) {
      this.loadingShuffledAds = true
      this.advertisements = []
      this.loadingAdvertisements = load

      await clearUniqueCreatives()

      const savedBetweenStart = +new Date(this.selectedDateRange?.start) || null
      const savedBetweenEnd = +new Date(this.selectedDateRange?.end) || null
      try {
        const { results, nextPage } = await ForeplayAPI.Ads.getShuffledDiscoveryAds(null, {
          'or.language': this.selectedLanguages.map(language => language.name),
          ...this.activeFilters,
          savedBetweenStart,
          savedBetweenEnd,
          textSearch: this.textSearch,
          sort: this.sortOrder[0].value
        })

        this.lastDocId = nextPage
        if (this.getUniqueCreatives) {
          const uniqueCreatives = await checkAndCacheAll(results)
          this.advertisements = [...this.advertisements, ...uniqueCreatives]
        } else {
          this.advertisements = [...this.advertisements, ...results]
        }
        this.loadingShuffledAds = false
      } catch (e) {
        console.log(e)
      } finally {
        this.loadingAdvertisements = false
        this.infiniteId += 1
      }
      this.isShuffled = true
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 600)
    },
    // Fetch Advertisements
    async fetchAdvertisements (load = true) {
      this.dropForYouIfFiltered()

      // Handle redundant calls
      if (this.loadingAdvertisements) return

      if (this.adsCancelToken) this.adsCancelToken.cancel()
      this.adsCancelToken = axios.CancelToken.source()
      this.advertisements = []
      this.loadingAdvertisements = load

      await clearUniqueCreatives()

      const savedBetweenStart = +new Date(this.selectedDateRange?.start) || null
      const savedBetweenEnd = +new Date(this.selectedDateRange?.end) || null
      try {
        const { results, nextPage } = await ForeplayAPI.Ads.getDiscoveryAds(null, {
          'or.language': this.selectedLanguages.map(language => language.name),
          ...this.activeFilters,
          savedBetweenStart,
          savedBetweenEnd,
          textSearch: this.textSearch,
          sort: this.sortOrder[0].value
        }, false, this.adsCancelToken?.token)

        this.lastDocId = nextPage
        if (this.getUniqueCreatives) {
          const uniqueCreatives = await checkAndCacheAll(results)
          this.advertisements = [...this.advertisements, ...uniqueCreatives]
        } else {
          this.advertisements = results
        }
      } catch (e) {
        console.log(e)
        if (e.name !== 'AbortError' && e.code !== 'ERR_CANCELED') {
          console.log(e)
        }
      } finally {
        this.loadingAdvertisements = false
        this.infiniteId += 1
      }
      this.isShuffled = false

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 600)
    },
    updateAdsLocal (adsToUpdate) {
      const duplicateAds = [...this.advertisements]

      adsToUpdate.forEach((ad) => {
        const index = duplicateAds.findIndex((a) => a.id === ad.id)

        if (index !== -1) {
          duplicateAds[index] = ad
        }
      })

      this.advertisements = duplicateAds
    },

    // ================================================================================
    // ================================== UI METHODS ==================================
    // ================================================================================

    toggleKeywordSearchMethod () {
      this.openKeywordSearch = !this.openKeywordSearch
    },
    updateSelectedLanguages (newSelectedLanguages) {
      this.selectedLanguages = newSelectedLanguages
      this.fetchAdvertisements()
    },
    startPosition (position) {
      this.$refs.bottomShadow.classList.add(`start${position}`)
    },
    onScroll (e, position) {
      const marginValue =
        position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding =
        position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5
      if (this.$refs.filterBar) {
        this.$refs.filterBar.style.marginLeft = `${marginValue}px`
        this.$refs.filterBar.style.marginRight = `${marginValue}px`
        this.$refs.filterBar.style.marginTop = `${marginValue}px`
        this.$refs.filterBar.style.borderRadius = `${rounding}px`
      }
      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue
          ? position.scrollTop / 15
          : maxValue
      }

      if (position.scrollTop && this.$refs.filterBar) {
        this.$refs.filterBar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(
        15
      )}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
        7
      )}px ${shadowSection(
        10
      )}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(
        7
      )}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
        3
      )}px ${shadowSection(5)}px`
      // this.$refs.filterBar.style.boxShadow = `rgba(58, 111, 251, 0.4) -${shadowSection(5)}px ${shadowSection(5)}px, rgba(58, 111, 251, 0.3) -${shadowSection(10)}px ${shadowSection(10)}px, rgba(58, 111, 251, 0.2) -${shadowSection(15)}px ${shadowSection(15)}px, rgba(58, 111, 251, 0.1) -${shadowSection(20)}px ${shadowSection(20)}px, rgba(58, 111, 251, 0.05) -${shadowSection(25)}px ${shadowSection(25)}px`
      } else {
        if (this.$refs.filterBar) {
          this.$refs.filterBar.style.boxShadow = null
        }
      }
    },
    moveShadowRight () {
      this.$refs.bottomShadow.classList.add('moveRight')

      this.$refs.bottomShadow.classList.remove('moveLeft')
      this.$refs.bottomShadow.classList.remove('moveCenter')
    },
    moveShadowCenter () {
      this.$refs.bottomShadow.classList.add('moveCenter')

      this.$refs.bottomShadow.classList.remove('moveLeft')
      this.$refs.bottomShadow.classList.remove('moveRight')
    },
    moveShadowLeft () {
      this.$refs.bottomShadow.classList.add('moveLeft')

      this.$refs.bottomShadow.classList.remove('moveRight')
      this.$refs.bottomShadow.classList.remove('moveCenter')
    },
    updateFilter (filterName, items) {
      this[filterName] = items
    }
  },
  metaInfo: {
    title: 'Foreplay.co | Discovery'
  }
}
</script>

<style scoped>
.brand-view-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none; /* Standard */
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
  white-space: nowrap;
}
.brand-view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.brand-view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
  font-weight: 500;
}
.counter-container {
  box-shadow: 0px 1px 1px -0.5px rgba(26, 48, 84, 0.04), 0px 2px 2px -1px rgba(26, 48, 84, 0.03), 0px 3px 3px -1.5px rgba(26, 48, 84, 0.03), 0px 5px 5px -2.5px rgba(26, 48, 84, 0.02), 0px 8px 8px -4px rgba(26, 48, 84, 0.02);
}

.shadow-elem {
  display: flex;
  justify-content: center;
  width: 34%;
}

.tool-el {
  /* Right */
  --right-width: 25%;
  --right-translate: 72%;

  /* Center */
  --center-width: 25%;
  --center-translate: 39%;

  /* Left */
  --left-width: 34%;
  --left-translate: 0%;
}

/* Right */
.moveRight {
  animation: moveRightAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}

.moveRight div {
  animation: rightWidthAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}

.startRight {
  transform: translateX(var(--right-translate));
}

.startRight div {
  width: var(--right-width)
}

/* Center */
.moveCenter {
  animation: moveCenterAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}

.moveCenter div {
  animation: centerWidthAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}

.startCenter {
  transform: translateX(var(--center-translate));
}

.startCenter div {
  width: var(--center-width)
}

/* Left */
.moveLeft {
  animation: moveLeftAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}

.moveLeft div {
  animation: leftWidthAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}

.startLeft {
  transform: translateX(var(--left-translate));
}

.startLeft div {
  width: var(--left-width)
}
.shuffleFade-enter-active, .shuffleFade-leave-active {
  transition: opacity 0.3s;
}
.shuffleFade-enter, .shuffleFade-leave-to {
  opacity: 0;
}

/* Rigth Animations */
@keyframes moveRightAnimation {
  to {
    transform: translateX(var(--right-translate));
  }
}

@keyframes rightWidthAnimation {
  to {
    width: var(--right-width)
  }
}

/* Center Animations */
@keyframes moveCenterAnimation {
  to {
    transform: translateX(var(--center-translate));
  }
}

@keyframes centerWidthAnimation {
  to {
    width: var(--center-width)
  }
}

/* Left Animations */
@keyframes moveLeftAnimation {
  to {
    transform: translateX(var(--left-translate));
  }
}

@keyframes leftWidthAnimation {
  to {
    width: var(--left-width)
  }
}

.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  background-color: transparent;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.secondary-btn:hover {
  box-shadow: none;
  background-color: #F6F8FA;
}
.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Vue <transition> classes */
</style>
